/**
 * Password recovery — the operator half (ADR-068).
 *
 * Two routes, and one property that shapes every caller of this module:
 *
 *   **The link is returned exactly once.** Only the SHA-256 of a token is ever
 *   stored, so nothing — not the server, not this panel, not a support agent
 *   with a database prompt — can re-read an issued link. `issuePasswordReset`
 *   MINTS A NEW token, supersedes the original request, and hands back a live
 *   credential in the response body. It is held in one component's state for as
 *   long as the dialog is open and dropped when it closes.
 *
 *   Therefore: never store it, never log it, never put it in a toast, never in
 *   a URL, never in `useAsync` (whose result outlives the render that asked for
 *   it). `PasswordResets.tsx` keeps all of that in one place; this module's job
 *   is only to fetch and to type.
 *
 * MOCK MODE HAS NO FIXTURES HERE, deliberately. Fake reset rows would teach an
 * operator to trust a queue that is not real, and a plausible-looking fake link
 * is a thing someone would eventually paste to a player. The read answers empty
 * and the write goes live and fails honestly — the same rule the key manager and
 * the support surface already keep.
 */
import { http } from './http.ts';
import { MOCK_MODE } from './client.ts';

/** One unresolved recovery request. Mirrors `PendingResetView` on the server. */
export interface PendingReset {
  id: number;
  userId: number;
  username: string;
  /** `k***l@example.com`, never the full address — masked by the server. */
  emailMasked: string | null;
  /** `self` (the player asked) or `operator` (a link was issued here). */
  source: string;
  deliveredVia: string | null;
  delivered: boolean;
  requestedIp: string | null;
  expiresAt: string;
  /** Expired rows stay in the queue on purpose — the person is still locked out. */
  expired: boolean;
  createdAt: string;
}

/** The one-time response. `link` is a live credential; see the header. */
export interface IssuedReset {
  resetId: number;
  /** The request this replaces. It leaves the queue and can never be consumed. */
  supersedes: number;
  userId: number;
  username: string;
  link: string;
  expiresAt: string;
}

/**
 * LIVE — `GET /internal/admin/password-resets`. Viewer role.
 *
 * Includes expired requests: a player who asked yesterday and telephoned today
 * is exactly who this queue is for.
 */
export async function fetchPendingResets(limit = 50): Promise<PendingReset[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ requests: PendingReset[] }>(
    '/internal/admin/password-resets',
    { limit },
  );
  return body.requests;
}

/**
 * LIVE — `POST /internal/admin/password-resets/:id/issue`. Approver role, the
 * reason is mandatory and audited (`player.password_reset_issue`).
 *
 * Always hits the network, mock mode included: a form that appears to issue a
 * link and does not is worse than one that errors.
 */
export async function issuePasswordReset(id: number, reason: string): Promise<IssuedReset> {
  return http.post<IssuedReset>(`/internal/admin/password-resets/${id}/issue`, { reason });
}
